import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { useAnalytics } from "@/features/analytics/context/AnalyticsProvider";
import { leftSidebarOpenAtom } from "@/utils/sidebarStore";
import { useAtomValue } from "jotai";
import { MessageSquarePlus } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDeleteSession, useRenameSession, useSessions } from "../api/sessions";
import { useStreams } from "../streams/StreamsProvider";
import type { SessionScope } from "../types";
import { SessionItem } from "./SessionItem";

type ChatSessionListProps = {
  /** Called after the user navigates (used to close the mobile sidebar). */
  onNavigate?: () => void;
  /** Render the "New Session" button (hidden when the rail supplies its own). */
  showNewButton?: boolean;
  /** Render the "Recents" label (hidden when the rail supplies its own). */
  showRecentsHeader?: boolean;
  /** Which session list to show. Defaults to the Paperclip chat scope. */
  scope?: SessionScope;
  /** Route prefix for navigating to a session (defaults to "/chat"). */
  routeBase?: string;
};

/**
 * The chat sessions list ("New Session" + Recents), extracted from the old
 * SessionSidebar so it can live inside the unified WorkspaceSidebar. Requires
 * StreamsProvider above it (present on the chat shell) for streaming indicators.
 */
export const ChatSessionList = ({
  onNavigate,
  showNewButton = true,
  showRecentsHeader = true,
  scope = "paperclip",
  routeBase = "/chat",
}: ChatSessionListProps) => {
  const navigate = useNavigate();
  const { sessionId: activeSessionId } = useParams<{ sessionId?: string }>();
  const {
    data: sessions,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useSessions(scope);
  const deleteSession = useDeleteSession();
  const renameSession = useRenameSession();
  const { abortStreamsForSession, isSessionStreaming } = useStreams();
  const { trackEvent } = useAnalytics();
  const [deleteSessionId, setDeleteSessionId] = useState<string | null>(null);
  const [revealedSessionId, setRevealedSessionId] = useState<string | null>(null);
  const [renameTarget, setRenameTarget] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [renameValue, setRenameValue] = useState("");

  // Reset revealed swipe state when the mobile sidebar closes
  const sidebarOpen = useAtomValue(leftSidebarOpenAtom);
  const prevSidebarOpen = useRef(sidebarOpen);
  useEffect(() => {
    if (prevSidebarOpen.current && !sidebarOpen) {
      setRevealedSessionId(null);
    }
    prevSidebarOpen.current = sidebarOpen;
  }, [sidebarOpen]);

  const handleReveal = useCallback((sessionId: string | null) => {
    setRevealedSessionId(sessionId);
  }, []);

  const handleNavigate = useCallback(
    (sessionId: string) => {
      setRevealedSessionId(null);
      navigate(`${routeBase}/${sessionId}`);
      onNavigate?.();
    },
    [navigate, onNavigate, routeBase],
  );

  const handleDelete = useCallback((sessionId: string) => {
    setRevealedSessionId(null);
    setDeleteSessionId(sessionId);
  }, []);

  const handleRename = useCallback((sessionId: string, currentTitle: string) => {
    setRevealedSessionId(null);
    setRenameTarget({ id: sessionId, title: currentTitle });
    setRenameValue(currentTitle);
  }, []);

  const confirmRename = () => {
    if (!renameTarget) return;
    const next = renameValue.trim();
    if (next && next !== renameTarget.title) {
      renameSession.mutate({ sessionId: renameTarget.id, title: next });
    }
    setRenameTarget(null);
  };

  const confirmDelete = () => {
    if (!deleteSessionId) return;
    trackEvent("delete_session");
    abortStreamsForSession(deleteSessionId);
    deleteSession.mutate(deleteSessionId);
    if (activeSessionId === deleteSessionId) {
      navigate(routeBase);
    }
    setDeleteSessionId(null);
  };

  return (
    <div className="flex h-full flex-col">
      {/* New chat */}
      {showNewButton && (
        <div className="px-3 pt-2 pb-2">
          <Button
            variant="ghost"
            size="sm"
            className="justify-start gap-2 text-zinc-700 hover:text-zinc-900 hover:bg-zinc-100"
            onClick={() => {
              navigate(routeBase);
              onNavigate?.();
            }}
          >
            <MessageSquarePlus className="h-4 w-4" />
            New session
          </Button>
        </div>
      )}

      {/* Recents */}
      <div className="flex-1 overflow-y-auto min-h-0 px-3">
        {showRecentsHeader && (
          <div className="px-1 pb-1.5 pt-1 text-xs font-medium text-zinc-500">
            Recents
          </div>
        )}

        {isLoading && (
          <div className="space-y-1 px-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-8 animate-pulse rounded-[3px] bg-zinc-100"
              />
            ))}
          </div>
        )}

        {sessions?.length === 0 && !isLoading && (
          <p className="px-2 py-4 text-center text-xs text-zinc-500">
            No conversations yet
          </p>
        )}

        <div className="space-y-px">
          {sessions?.map((session) => (
            <SessionItem
              key={session.session_id}
              sessionId={session.session_id}
              title={session.title || "Untitled"}
              isActive={activeSessionId === session.session_id}
              isStreaming={isSessionStreaming(session.session_id)}
              revealedSessionId={revealedSessionId}
              onReveal={handleReveal}
              onNavigate={handleNavigate}
              onDelete={handleDelete}
              onRename={handleRename}
            />
          ))}
        </div>

        {hasNextPage && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fetchNextPage()}
            loading={isFetchingNextPage}
            className="mx-auto mt-2 text-xs text-zinc-500 hover:text-zinc-700"
          >
            {isFetchingNextPage ? "Loading..." : "Load older"}
          </Button>
        )}
      </div>

      <Dialog open={deleteSessionId !== null} onOpenChange={(open) => !open && setDeleteSessionId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete conversation?</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setDeleteSessionId(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={renameTarget !== null}
        onOpenChange={(open) => !open && setRenameTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle id="rename-conversation-title">
              Rename conversation
            </DialogTitle>
          </DialogHeader>
          <Input
            autoFocus
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                confirmRename();
              }
            }}
            placeholder="Conversation name"
            aria-labelledby="rename-conversation-title"
          />
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setRenameTarget(null)}>
              Cancel
            </Button>
            <Button onClick={confirmRename} disabled={!renameValue.trim()}>
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
